import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, FileText, ArrowRight, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'fundle_industry_report_dismissed_v1';
const SHOW_DELAY_MS = 12000;

const FEATURED_BRANDS = [
  'Loyalty strategy',
  'AI & churn',
  'WhatsApp journeys',
  'Mall & coalition',
  'Measurement & ROI',
];

const IndustryReportPopup = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === 'true') return;
    } catch (e) {
      // localStorage may be unavailable (private mode); fall through and still show
    }
    const timer = setTimeout(() => setShow(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch (e) {
      // ignore
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-[#0B1437]/60 backdrop-blur-sm animate-in fade-in duration-300"
      data-testid="industry-report-popup"
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          aria-label="Close report popup"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-[#4A5878] flex items-center justify-center z-10 transition-colors"
          data-testid="industry-report-popup-close"
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div className="bg-[#0B1437] px-6 sm:px-8 pt-8 pb-6 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-[11px] font-semibold uppercase tracking-wider mb-4">
            <Sparkles size={12} /> New • India Retail Loyalty Report 2026
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold leading-tight mb-2">
            The State of Retail Loyalty in India — 2026
          </h2>
          <p className="text-indigo-50 text-sm sm:text-base leading-relaxed">
            An operator-grade view of loyalty, AI and measurement for India's leading brands and malls — practical frameworks, not vendor hype.
          </p>
        </div>

        {/* Body */}
        <div className="px-6 sm:px-8 py-6">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">What's inside</p>
          <div className="flex flex-wrap gap-2 mb-6" data-testid="industry-report-popup-brands">
            {FEATURED_BRANDS.map((b, i) => (
              <span
                key={i}
                className="px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-[11px] sm:text-xs font-semibold text-[#1F2A4A]"
              >
                {b}
              </span>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/downloads"
              onClick={dismiss}
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#0B1437] hover:bg-[#050A1F] text-white font-semibold text-sm shadow-lg shadow-[#D4AF37]/30 transition-colors"
              data-testid="industry-report-popup-download"
            >
              <FileText size={16} /> Download Full Report
              <ArrowRight size={14} />
            </Link>
            <Link
              to="/contact"
              onClick={dismiss}
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 hover:border-[#D4AF37] hover:text-[#0B1437] text-[#1F2A4A] font-semibold text-sm transition-colors"
              data-testid="industry-report-popup-demo"
            >
              Talk to a Loyalty Expert
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustryReportPopup;
